import React, { useEffect, useMemo, useRef, useState } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DropdownFilterOption = {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
};

export interface DropdownFilterProps {
  children?: React.ReactNode;
  className?: string;
  clearIcon?: React.ReactNode;
  disabled?: boolean;
  errorText?: string | null;
  filterLabel?: React.ReactNode;
  filterOptionsHeading?: string;
  filterPresetHeading?: string;
  helper?: React.ReactNode;
  icon?: React.ReactNode;
  isMulti?: boolean;
  labelContent?: React.ReactNode;
  name: string;
  onChange?: (name: string, value: string[] | string | null) => void;
  onError?: ((error: unknown) => void) | null;
  options?: DropdownFilterOption[];
  optionsHeightLimit?: boolean;
  preset?: React.ReactNode;
  required?: boolean;
  right?: boolean;
  selectAllIcon?: React.ReactNode;
  small?: boolean;
  validator?: unknown;
  value?: string[] | string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/**
 * ":ALL" badge when nothing (or everything) is selected, otherwise a
 * count badge — mirrors the AceUp component library's original behavior.
 */
const MultiSelectBadge: React.FC<{ options: DropdownFilterOption[]; selected: string[] }> = ({
  options,
  selected,
}) => {
  if (!selected.length || selected.length === options.length) {
    return <span className="text-uppercase">:all</span>;
  }
  return <span className="badge-tag-filter">{selected.length}</span>;
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Local re-implementation of `@aceup/components`' `DropdownFilter` — a
 * single/multi-select popover filter control. This keeps the same public
 * prop contract and the same DOM class names
 * (`dropdown-filter`, `sub-menu`, `dropdown-filter-button`,
 * `dropdown-filter-options`, `au-f--lb-*`, `badge-tag-filter`, …) that
 * `src/styles/index.css` already styles, so the control keeps its current
 * look with no CSS changes needed. It intentionally does not port the
 * library's internal SubMenu/InputWrapper/Badge/Button/Icon components —
 * those pull in an icon-font system and a Sass toolchain this project
 * doesn't have; the pieces actually used here (single/multi select,
 * required, disabled, a leading icon, a "Select All"/"Reset Filter" pair
 * for multi-select) are reimplemented directly against plain React + the
 * project's own CSS.
 */
const DropdownFilter: React.FC<DropdownFilterProps> = ({
  children = null,
  className,
  clearIcon = null,
  disabled = false,
  errorText = null,
  filterLabel = '',
  filterOptionsHeading = '',
  filterPresetHeading = '',
  helper = null,
  icon = null,
  isMulti = false,
  labelContent = null,
  name,
  onChange,
  onError = null,
  options = [],
  optionsHeightLimit = false,
  preset = null,
  required = false,
  right = false,
  selectAllIcon = null,
  small = false,
  value = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string | null>(
    typeof value === 'string' ? value : null
  );
  const [checkboxValues, setCheckboxValues] = useState<string[]>(Array.isArray(value) ? value : []);
  const [touched, setTouched] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMulti) {
      setCheckboxValues(Array.isArray(value) ? value : []);
    } else {
      setInputValue(typeof value === 'string' ? value : null);
    }
  }, [isMulti, value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const optionsMap = useMemo(() => Object.fromEntries(options.map((o) => [o.value, o.label])), [
    options,
  ]);

  const isEmpty = isMulti ? checkboxValues.length === 0 : !inputValue;
  const showError = Boolean(required && touched && isEmpty);

  useEffect(() => {
    if (showError) onError?.(errorText ?? `${name} is required`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError]);

  const handleSingleSelect = (optionValue: string) => {
    setInputValue(optionValue);
    setTouched(true);
    onChange?.(name, optionValue);
    setIsOpen(false);
  };

  const handleMultiToggle = (optionValue: string) => {
    const next = checkboxValues.includes(optionValue)
      ? checkboxValues.filter((v) => v !== optionValue)
      : [...checkboxValues, optionValue];
    setCheckboxValues(next);
    setTouched(true);
    onChange?.(name, next);
  };

  const handleSelectAll = () => {
    const selectable = options.filter((o) => !o.disabled).map((o) => o.value);
    setCheckboxValues(selectable);
    setTouched(true);
    onChange?.(name, selectable);
  };

  const handleReset = () => {
    setCheckboxValues([]);
    setTouched(true);
    onChange?.(name, []);
  };

  const allSelectableSelected =
    options.filter((o) => !o.disabled).every((o) => checkboxValues.includes(o.value)) &&
    options.some((o) => !o.disabled);

  const buttonLabel = isMulti ? filterLabel : optionsMap[inputValue ?? ''] ?? filterLabel;

  const optionsList = (
    <div className="au-f--lb-options">
      {options.map((option) => (
        <label key={option.value} className={isMulti ? 'au-f--lb-checkbox' : 'au-f--lb-radio'}>
          <input
            type={isMulti ? 'checkbox' : 'radio'}
            name={name}
            value={option.value}
            disabled={option.disabled}
            checked={isMulti ? checkboxValues.includes(option.value) : inputValue === option.value}
            onChange={() =>
              isMulti ? handleMultiToggle(option.value) : handleSingleSelect(option.value)
            }
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );

  const filterOptions = (
    <>
      {optionsList}

      {isMulti && (
        <button
          type="button"
          className="au-btn au-btn-outline au-btn-sm"
          disabled={allSelectableSelected}
          onClick={handleSelectAll}
        >
          {selectAllIcon}
          Select All
        </button>
      )}

      {isMulti && checkboxValues.length > 0 && (
        <button type="button" className="au-btn au-btn-ghost au-btn-sm" onClick={handleReset}>
          {clearIcon}
          Reset Filter
        </button>
      )}
    </>
  );

  return (
    <div className={cx('dropdown-filter', small && 'dropdown-filter-small', className)}>
      {labelContent && <span className="dropdown-filter-label-content">{labelContent}</span>}

      <div
        ref={rootRef}
        className={cx('sub-menu', right && 'sub-menu-right', isOpen && 'sub-menu-opened')}
      >
        <button
          type="button"
          className="au-btn dropdown-filter-button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => !disabled && setIsOpen((o) => !o)}
        >
          <span className="btn-children">
            {icon}
            <span className="dropdown-filter-label">
              <span className="dropdown-filter-label-text">{buttonLabel}</span>
            </span>
            {isMulti && <MultiSelectBadge options={options} selected={checkboxValues} />}
            <span className="caret-down" />
          </span>
        </button>

        {isOpen && (
          <div
            className={cx(
              'sub-menu-container',
              'dropdown-filter-options',
              optionsHeightLimit && 'dropdown-filter-options-height-limit'
            )}
          >
            {preset ? (
              <div className="dropdown-filter-options-row">
                <div className="dropdown-filter-options-col">
                  <div className="dropdown-filter-options-heading">{filterPresetHeading}</div>
                  {preset}
                </div>
                <div className="dropdown-filter-options-col">
                  <div className="dropdown-filter-options-heading">{filterOptionsHeading}</div>
                  {filterOptions}
                </div>
              </div>
            ) : (
              filterOptions
            )}
          </div>
        )}
      </div>

      {children}

      {showError && errorText && <p className="dropdown-filter-error">{errorText}</p>}
      {helper && <p className="dropdown-filter-helper">{helper}</p>}
    </div>
  );
};

export default DropdownFilter;
